import { Component } from 'react';
import { customAlphabet } from 'nanoid';
import { GeneratorLayout, genStyles } from '../converter-layout/generator-layout';

const ALPHABETS = {
    'url-safe': 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789_-',
    'alphanumeric': 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789',
    'numeric': '0123456789',
    'lowercase': 'abcdefghijklmnopqrstuvwxyz',
    'uppercase': 'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
    'hex': '0123456789abcdef',
    'no-look-alike': 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789',
};

interface State {
    alphabet: keyof typeof ALPHABETS;
    customAlpha: string;
    length: number;
    count: number;
    output: string;
    copied: boolean;
}

export default class NanoIdGeneratorComponent extends Component<{ feature?: string }, State> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;
    state: State = {
        alphabet: 'url-safe',
        customAlpha: '',
        length: 21,
        count: 5,
        output: '',
        copied: false,
    };

    componentDidMount() {
        this.generate();
    }

    componentWillUnmount() {
        if (this.copyTimeout) clearTimeout(this.copyTimeout);
    }

    private collisionEstimate(alphaSize: number, len: number): string {
        const bits = Math.log2(alphaSize) * len;
        const years = Math.pow(2, bits / 2) / (1e6 * 60 * 60 * 24 * 365.25);
        if (years > 1e9) return `~${(years / 1e9).toExponential(1)} billion years (1M IDs/sec)`;
        if (years > 1000) return `~${(years / 1000).toFixed(1)}k years (1M IDs/sec)`;
        if (years > 1) return `~${years.toFixed(1)} years (1M IDs/sec)`;
        if (years * 365 > 1) return `~${(years * 365).toFixed(1)} days (1M IDs/sec)`;
        return `~${(years * 365 * 24 * 3600).toFixed(0)} seconds (1M IDs/sec)`;
    }

    private get effectiveAlphabet(): string {
        const a = ALPHABETS[this.state.alphabet];
        return a || this.state.customAlpha;
    }

    generate = () => {
        const alpha = this.effectiveAlphabet;
        if (!alpha) {
            this.setState({ output: '(no alphabet)', copied: false });
            return;
        }
        const gen = customAlphabet(alpha, this.state.length);
        const ids: string[] = [];
        for (let i = 0; i < this.state.count; i++) ids.push(gen());
        this.setState({ output: ids.join('\n'), copied: false });
    };

    copyOutput = () => {
        if (!this.state.output) return;
        navigator.clipboard.writeText(this.state.output).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copied: true });
            this.copyTimeout = setTimeout(() => this.setState({ copied: false }), 2000);
        });
    };

    render() {
        const { alphabet, length, count, output, copied } = this.state;
        const alpha = this.effectiveAlphabet;
        const idCount = output ? output.split('\n').filter(Boolean).length : 0;

        const form = (
            <div className={genStyles.section}>
                <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Options</span></div>
                <div className={genStyles.field}>
                    <label htmlFor="nid-alphabet">Alphabet</label>
                    <select id="nid-alphabet" value={alphabet}
                        onChange={e => this.setState({ alphabet: e.target.value as any }, this.generate)}>
                        {Object.keys(ALPHABETS).map(k => <option key={k} value={k}>{k}</option>)}
                    </select>
                </div>
                <div className={genStyles.fieldRow}>
                    <div className={genStyles.field}>
                        <label htmlFor="nid-length">Length</label>
                        <input id="nid-length" type="number" inputMode="decimal" min={4} max={128} value={length}
                            onChange={e => this.setState({ length: Math.max(4, Math.min(128, +e.target.value || 21)) }, this.generate)} />
                    </div>
                    <div className={genStyles.field}>
                        <label htmlFor="nid-count"># of IDs</label>
                        <input id="nid-count" type="number" inputMode="decimal" min={1} max={1000} value={count}
                            onChange={e => this.setState({ count: Math.max(1, Math.min(1000, +e.target.value || 5)) }, this.generate)} />
                    </div>
                </div>
                <button className={`${genStyles.iconBtn} ${genStyles.iconBtnPrimary}`} onClick={this.generate} style={{ alignSelf: 'flex-start' }}>
                    ↻ Regenerate
                </button>
                <p style={{ fontSize: '0.78rem', color: '#64748b', margin: 0 }}>
                    <strong>Collision odds:</strong> {this.collisionEstimate(alpha.length, length)} until 1% chance of any collision.
                </p>
            </div>
        );

        return (
            <GeneratorLayout
                form={form}
                previewLabel="Generated IDs"
                previewMeta={`${idCount} ID${idCount === 1 ? '' : 's'}`}
                preview={<pre className={genStyles.previewArea} aria-label="Generated IDs">{output}</pre>}
                previewActions={
                    <button className={`${genStyles.iconBtn} ${copied ? genStyles.iconBtnSuccess : ''}`}
                        onClick={this.copyOutput} disabled={!output}>
                        {copied ? '✓ Copied' : '📋 Copy all'}
                    </button>
                }
            />
        );
    }
}
